import React, { useCallback, useEffect, useRef, useState } from 'react';
import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import {
  DEFAULT_NUTRIENT_FORMULAS,
  buildCalculationRows,
  buildReverseRows,
} from '../../utils/nutrientCalculator';

const SAMPLE_TYPE = 'SOIL';
const CALC_DEBOUNCE_MS = 1000;

const NutrientCalculator = () => {
  const [mode, setMode] = useState('forward');
  const [inputsById, setInputsById] = useState({});
  const [formulaRows, setFormulaRows] = useState([]);
  const [calculatedRows, setCalculatedRows] = useState([]);
  const [loadingFormulas, setLoadingFormulas] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [error, setError] = useState('');

  const isReverse = mode === 'reverse';
  const calcRequestId = useRef(0);

  const loadFormulas = useCallback(async () => {
    setLoadingFormulas(true);
    setError('');
    try {
      const response = await axiosInstance.get('/nutrient-calculator/formulas', {
        params: { sampleType: SAMPLE_TYPE },
      });
      setFormulaRows(response.data?.data || []);
      setUseFallback(false);
    } catch {
      setUseFallback(true);
      setFormulaRows([]);
      setError('Could not load DB formulas. Using offline static formulas.');
    } finally {
      setLoadingFormulas(false);
    }
  }, []);

  useEffect(() => {
    loadFormulas();
  }, [loadFormulas]);

  useEffect(() => {
    if (loadingFormulas) {
      return undefined;
    }

    if (useFallback) {
      const rows = isReverse
        ? buildReverseRows(DEFAULT_NUTRIENT_FORMULAS, inputsById)
        : buildCalculationRows(DEFAULT_NUTRIENT_FORMULAS, inputsById);
      setCalculatedRows(rows);
      return undefined;
    }

    const hasAnyInput = Object.values(inputsById).some(
      (value) => value != null && String(value).trim() !== '',
    );

    if (!hasAnyInput) {
      setCalculatedRows(
        formulaRows.map((row) => ({
          ...row,
          rawInput: '',
          targetValue: '',
          result: '',
          computedRaw: '',
        })),
      );
      return undefined;
    }

    const requestId = calcRequestId.current + 1;
    calcRequestId.current = requestId;

    const timer = setTimeout(async () => {
      setCalculating(true);
      try {
        const response = await axiosInstance.post('/nutrient-calculator/calculate', {
          mode: isReverse ? 'reverse' : 'forward',
          sampleType: SAMPLE_TYPE,
          inputs: inputsById,
        });
        if (calcRequestId.current !== requestId) {
          return;
        }
        setCalculatedRows(response.data?.data || []);
        setError('');
      } catch {
        if (calcRequestId.current === requestId) {
          setError('Calculation failed. Check your inputs and try again.');
        }
      } finally {
        if (calcRequestId.current === requestId) {
          setCalculating(false);
        }
      }
    }, CALC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputsById, isReverse, formulaRows, loadingFormulas, useFallback]);

  const rows = calculatedRows.length > 0 || useFallback
    ? calculatedRows
    : formulaRows;

  const handleInputChange = (id, value) => {
    setInputsById((prev) => ({ ...prev, [id]: value }));
  };

  const handleClearAll = () => {
    setInputsById({});
  };

  const toggleMode = () => {
    setMode((prev) => (prev === 'forward' ? 'reverse' : 'forward'));
    setInputsById({});
    setCalculatedRows([]);
  };

  const inputColumnLabel = isReverse ? 'Updated value' : 'Raw input';
  const outputColumnLabel = isReverse ? 'Raw value' : 'Result';
  const inputPlaceholder = isReverse ? 'Enter updated value' : 'Enter raw value';
  const headerHint = isReverse
    ? 'Enter updated values to back-calculate raw sensor readings (static inverse formulas).'
    : 'Enter raw sensor readings — results use the same DB formulas as create report.';
  const modeButtonLabel = isReverse ? 'Get updated values' : 'Get raw values';

  const getInputValue = (row) => inputsById[row.id] ?? '';
  const getOutputValue = (row) => (isReverse ? row.computedRaw : row.result);
  const getFormulaDisplay = (row) => {
    if (isReverse) {
      return row.reverseFormula || row.formula || '—';
    }
    return row.formula || '—';
  };

  return (
    <Wrapper>
      <div className="pb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 w-100">
          <div className="text-start">
            <h2 className="m-0 fw-bold text-start">Nutrient Calculator</h2>
            <p className="text-muted mb-0 mt-1 text-start">{headerHint}</p>
            {!useFallback && !loadingFormulas && (
              <p className="text-muted small mb-0 mt-1">
                Forward: dynamic formulas from parameter_formulas ({SAMPLE_TYPE}).
              </p>
            )}
          </div>
          <div className="d-flex flex-shrink-0 align-items-center gap-2 ms-auto">
            {calculating && (
              <span className="text-muted small">Calculating…</span>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClearAll}
            >
              Clear all
            </button>
            <button
              type="button"
              className={`btn btn-sm ${isReverse ? 'btn-warning' : 'btn-dark'}`}
              onClick={toggleMode}
            >
              {modeButtonLabel}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning py-2 small" role="alert">
            {error}
          </div>
        )}

        {loadingFormulas ? (
          <div className="text-center py-5 text-muted">Loading formulas…</div>
        ) : (
          <>
            <div className="d-none d-md-block bg-white border rounded-3 shadow-sm overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Parameter</th>
                      <th style={{ minWidth: 140 }}>{inputColumnLabel}</th>
                      <th style={{ minWidth: 120 }}>{outputColumnLabel}</th>
                      <th>Unit</th>
                      <th>Formula</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="fw-medium">{row.parameter}</td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control form-control-sm"
                            placeholder={inputPlaceholder}
                            value={getInputValue(row)}
                            onChange={(e) => handleInputChange(row.id, e.target.value)}
                          />
                        </td>
                        <td>
                          <code className="d-block px-2 py-1 bg-light rounded small">
                            {getOutputValue(row) || '—'}
                          </code>
                        </td>
                        <td className="text-muted">{row.unit}</td>
                        <td>
                          <code className="small text-break">{getFormulaDisplay(row)}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="d-md-none">
              {rows.map((row) => (
                <div key={row.id} className="card mb-3 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="card-title mb-0 fw-semibold">{row.parameter}</h6>
                      <span className="badge text-bg-secondary">{row.unit}</span>
                    </div>
                    <label className="form-label small mb-1">{inputColumnLabel}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control form-control-sm mb-2"
                      placeholder={inputPlaceholder}
                      value={getInputValue(row)}
                      onChange={(e) => handleInputChange(row.id, e.target.value)}
                    />
                    <label className="form-label small mb-1">{outputColumnLabel}</label>
                    <code className="d-block px-2 py-2 bg-light rounded small mb-2">
                      {getOutputValue(row) || '—'}
                    </code>
                    <details>
                      <summary className="small text-muted">View formula</summary>
                      <code className="d-block mt-2 small text-break">{getFormulaDisplay(row)}</code>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default NutrientCalculator;